import { ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { LinkCard } from "@amzn/stencil-react-components/card";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { IconBin } from "@amzn/stencil-react-icons";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { getString } from "common/uistringlabels/uiStringUtils";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC } from "react";
import { useHistory } from "react-router-dom";

export interface ListItemProps {
  listId: string;
  listName: string;
  listType: ListType;
  item: ListItemType;
  allowDeletion?: boolean;
}

export interface ListItemType {
  taxonomyId: string;
  name: string;
  description: string;
  linkToDetails: string;
  logoUrl?: string;
}

export const ListItem: FC<ListItemProps> = ({ listId, listName, listType, item, allowDeletion }) => {
  const history = useHistory();
  const { removeItemFromList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();

  if (!item) {
    return (
      <View height="78px" width="100%" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="T200" color="neutral70">
          Item not found
        </Text>
      </View>
    );
  }
  const handleConfirmDelete = async (close: () => void) => {
    try {
      await removeItemFromList(listId, item.taxonomyId, listType);
      addMessageBanner(getString("lists.removeItemSuccess"), MessageBannerType.Success, 5000);
      close();
    } catch (error) {
      addMessageBanner(getString("lists.removeItemError"), MessageBannerType.Error);
      close();
    }
  };

  const renderDeleteModal = ({ close }: RenderModalFunctionParams) => (
    <ModalContent
      titleText={getString("lists.deleteConfirmation.title")}
      buttons={[
        <Button key="cancel" touchpoint="list-item-delete-cancel" variant={ButtonVariant.Secondary} onClick={close}>
          {getString("lists.deleteConfirmation.cancel")}
        </Button>,
        <Button
          key="confirm"
          touchpoint="list-item-delete-confirm"
          variant={ButtonVariant.Primary}
          onClick={() => handleConfirmDelete(close)}
        >
          {getString("lists.deleteConfirmation.confirm")}
        </Button>,
      ]}
    >
      <Text>{getString("lists.deleteConfirmation.item.message", { name: item.name, listName: listName })}</Text>
    </ModalContent>
  );

  return (
    <LinkCard href={item.linkToDetails} target="_blank" width={"100%"}>
      <Row height={"78px"} justifyContent="space-between" width={"100%"} gridGap="S250">
        {item.logoUrl && (
          <View
            display="flex"
            height={"78px"}
            padding={"S100"}
            alignItems="flex-start"
            className="img-square-wrapper"
            style={{ borderRadius: "16px" }}
          >
            <img
              alt={item.name}
              src={item.logoUrl}
              data-testid="catalog-item-url"
              style={{
                borderRadius: "16px",
                width: "70px",
                height: "70px",
                objectFit: "contain",
              }}
            />
          </View>
        )}
        <Col>
          <Text fontWeight="bold" fontSize="T200">
            {item.name}
          </Text>
          <Text
            fontSize="T100"
            style={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              height: "46px",
            }}
          >
            {item.description}
          </Text>
        </Col>
        {allowDeletion && (
          <Col justifyContent="center">
            <WithModal renderModal={renderDeleteModal}>
              {({ open }) => (
                <Button
                  touchpoint="list-item-delete-button"
                  icon={<IconBin color={"red70"} />}
                  variant={ButtonVariant.Tertiary}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();
                    open();
                  }}
                />
              )}
            </WithModal>
          </Col>
        )}
      </Row>
    </LinkCard>
  );
};
